import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import styled from "styled-components";
import InputGroup from 'react-bootstrap/InputGroup';
import Col from 'react-bootstrap/Col';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { VbButton } from "../VbButton";
import InputGroupText from "react-bootstrap/esm/InputGroupText";
import { FaSearch } from "react-icons/fa";
import { VbFormLabel, VbFormFeedback } from "../VbControls";
import { convertToMd5, cpfIsValid, emailIsValid } from "../../commons/utils";
import "./styles.css";

const VbTextFixed = styled(InputGroup.Text)`
`

interface VbInputProps {
    id: any;
    type: any;
    label?: String;
    placeholder?: any;
    defaultValue?: any;
    feedback?: String;
    textfixed?: String;
    required?: any;
    md?: String;
    ref?: any;
    size?: any;
    onAfterChange?: any;
    search?: any;
    disabled?: any;
    model?: any;
    validated?: any;
    textarea?: any;
    rows?: any;
    maxLength?: any;
    noDebounce?: boolean
}

export const Input: React.FC<VbInputProps> = forwardRef((props: VbInputProps, ref: any) => {
    const inputRef = useRef<any>();
    const [value, setValue] = useState<any>(props.defaultValue ? props.defaultValue : '');
    const [showPassword, setShowPassword] = useState<any>();
    const [error, setError] = useState<any>(false);
    const [disabled, setDisabled] = useState<any>(props.disabled);

    const debounceTimeoutRef = useRef<any>(null);

    function getValue() {
        return props.type === 'password' ? convertToMd5(value) : value;
    }

    function getId() {
        return props.id;
    }

    function validate(_value?: any) {
        _value = _value ? _value : value;

        if (!_value && !props.required)
            return true

        var result = props.required ? (_value !== '') : true;

        if (result && props.model) {
            if (props.model === 'phone')
                result = _value.length >= 14;
            else if (props.model === 'cpf')
                result = cpfIsValid(_value);
            else if (props.model === 'email')
                result = emailIsValid(_value)

            inputRef.current.setCustomValidity(result ? '' : 'Invalid field')
        }

        return result
    }

    useImperativeHandle(ref, () => ({
        getValue,
        setValue,
        setError,
        getId,
        validate,
        setDisabled
    }));

    function applyCustomMask(_value: string, mask: any): string {
        if (!mask)
            return _value;

        let result = '';
        let maskIndex = 0;

        for (let i = 0; i < _value.length; i++) {
            const char = _value[i];

            if (maskIndex >= mask.length) {
                break;
            }

            if (/\d/.test(char)) {
                while (maskIndex < mask.length && (mask[maskIndex] === '(' || mask[maskIndex] === ')' || mask[maskIndex] === ' ' || mask[maskIndex] === '-' || mask[maskIndex] === '.')) {
                    result += mask[maskIndex];
                    maskIndex++;
                }

                if (mask[maskIndex] === '9') {
                    result += char;
                    maskIndex++;
                }
            } else if (mask[maskIndex] === char) {
                result += char;
                maskIndex++;
            }
        }

        return result;
    }

    function validateModel() {
        if ((props.model === 'phone') && (value.length < 14))
            return 'Telefone inválido'
        else if ((props.model === 'cpf') && (!cpfIsValid(value)))
            return 'CPF inválido'
        else if ((props.model === 'email') && (!emailIsValid(value)))
            return 'E-mail inválido'
    }

    function getModel(_value: any) {
        if (props.model) {
            if (props.model === 'phone') {
                if (_value.length <= 14)
                    return '(99) 9999-9999'
                else
                    return '(99) 99999-9999'
            }
            else if (props.model === 'cpf')
                return '999.999.999-99'
            else if (props.model === 'cep')
                return '99999-999'
        } else
            return ''
    }

    function onChange(e: any) {
        const _value = e.target.value;
        const _maskedValue = applyCustomMask(_value, getModel(_value));

        setValue(_maskedValue);

        if (props.noDebounce) {
            props.onAfterChange?.(_maskedValue);
            validate(_value)
            return
        }

        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        debounceTimeoutRef.current = setTimeout(() => {
            if (props.onAfterChange) {
                props.onAfterChange(_maskedValue);
            }
        }, 300);

        validate(_value)
    }

    function onClick() {
        if (['date', 'datetime-local', 'time', 'month', 'week'].includes(props.type) && !disabled) {
            try {
                inputRef.current?.showPicker?.();
            } catch { }
        }
    }

    return (
        <Form.Group as={Col} md={props?.md ? props.md : "12"} controlId={props.id}>
            {props.label && <VbFormLabel>{(props?.required ? '*' : '') + props.label}</VbFormLabel>}
            <InputGroup size={props.size ? props.size : "sm"} hasValidation>
                <Form.Control
                    ref={inputRef}
                    as={props.textarea && 'textarea'}
                    name={props.id}
                    type={props.type !== 'password' ? props.type : showPassword ? 'text' : 'password'}
                    placeholder={props.placeholder}
                    onChange={onChange}
                    onClick={onClick}
                    required={props.required}
                    isInvalid={error}
                    autoComplete="off"
                    disabled={disabled}
                    value={value}
                    rows={props.rows}
                    maxLength={props.maxLength}
                />

                {props.search &&
                    <InputGroupText>
                        <FaSearch />
                    </InputGroupText>
                }

                {props.type === 'password' &&
                    <VbButton
                        size={props.size ? props.size : "sm"}
                        style={{
                            display: 'flex',
                            borderTopRightRadius: '4px',
                            borderBottomRightRadius: '4px',
                            fontSize: '0.875rem',
                        }}
                        onClick={() => setShowPassword(!showPassword)}
                    >
                        {showPassword ? <VisibilityOffIcon style={{ fontSize: "1.2rem" }} /> : <VisibilityIcon style={{ fontSize: "1.2rem" }} />}
                    </VbButton>
                }

                <VbFormFeedback type="invalid">
                    {
                        (props.model && props.validated && value) ? validateModel() :
                            !error &&
                            (props.feedback ? props.feedback : props.required ? 'Campo obrigatório.' : '')
                    }
                </VbFormFeedback>

                {props.textfixed &&
                    <VbTextFixed id="basic-addon2">
                        {props.textfixed}
                    </VbTextFixed>}
            </InputGroup>
        </Form.Group>
    );
});
